import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, OpenInFull } from "@mui/icons-material";
import { Button, Card, CardContent, CardHeader, Stack } from "../../design-system";
import { colors, spacing } from "../../design-system";

const getNoteDate = (note) => note?.updatedAt || note?.createdAt || note?.fecha || null;

const NotesTimeline = ({ notes = [], onOpenNote }) => {
  const { t, i18n } = useTranslation('scouting');
  const [expandedId, setExpandedId] = useState(null);

  const groupedNotes = useMemo(() => {
    const noDateLabel = t('components.notesTimeline.noDate');
    const groups = notes.reduce((accumulator, note) => {
      const rawDate = getNoteDate(note);
      const parsed = rawDate ? new Date(rawDate) : null;
      const groupLabel = (!parsed || Number.isNaN(parsed.getTime()))
        ? noDateLabel
        : parsed.toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" });
      if (!accumulator[groupLabel]) {
        accumulator[groupLabel] = [];
      }
      accumulator[groupLabel].push(note);
      return accumulator;
    }, {});

    return Object.entries(groups)
      .map(([label, grouped]) => ({
        label,
        sortValue: label === noDateLabel ? 0 : (() => {
          const firstDate = getNoteDate(grouped[0]);
          const parsed = firstDate ? new Date(firstDate) : null;
          return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0;
        })(),
        notes: grouped.sort(
          (left, right) => new Date(getNoteDate(right) || 0).getTime() - new Date(getNoteDate(left) || 0).getTime()
        ),
      }))
      .sort((left, right) => right.sortValue - left.sortValue);
  }, [notes, t, i18n.language]);

  return (
    <Card className="sc-timeline" variant="outlined">
      <CardHeader
        title={t('components.notesTimeline.title')}
        subheader={t('components.notesTimeline.subtitle')}
      />
      <CardContent>
        {groupedNotes.length === 0 && <Typography color="text.secondary">{t('notes.emptyDesc')}</Typography>}
        <Stack spacing={3}>
          {groupedNotes.map((group) => (
            <Box key={group.label}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Chip label={group.label} color="primary" variant="outlined" />
                <Typography variant="caption" color={'text.secondary'}>
                  {t('components.notesTimeline.notes_other', { count: group.notes.length })}
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                {group.notes.map((note, index) => {
                  const noteKey = note.id || `${note.jugadorId || 'jugador'}-${note.partidoId || index}`;
                  const isExpanded = expandedId === noteKey;
                  const rawDate = getNoteDate(note);
                  const parsedTs = rawDate ? new Date(rawDate) : null;
                  const timestampLabel = (!parsedTs || Number.isNaN(parsedTs.getTime()))
                    ? t('components.notesTimeline.noTimestamp')
                    : parsedTs.toLocaleString(i18n.language, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
                  return (
                    <Box
                      key={noteKey}
                      sx={{
                        borderRadius: spacing.borderRadius.lg,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <ButtonBase
                        aria-expanded={isExpanded}
                        aria-controls={`scouting-note-${noteKey}`}
                        onClick={() => setExpandedId(isExpanded ? null : noteKey)}
                        sx={{
                          width: "100%",
                          textAlign: "left",
                          p: 2,
                          display: "block",
                          borderRadius: spacing.borderRadius.lg,
                        }}
                      >
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
                          <Avatar sx={{ bgcolor: colors.primary[100], color: colors.primary[700] }}>
                            {note.jugadorNombre?.charAt(0)?.toUpperCase() || "?"}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                              <Typography variant="body1" fontWeight={700}>
                                {note.jugadorNombre || t('components.notesTimeline.noName')}
                              </Typography>
                              {note.competicion && <Chip label={note.competicion} size="small" variant="outlined" />}
                            </Stack>
                            <Typography variant="body2" color={'text.secondary'} sx={{ mt: 0.5 }}>
                              {note.equipoLocal || "Local"} vs {note.equipoVisitante || "Visitante"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color={'text.secondary'}
                              sx={{
                                mt: 1,
                                display: "-webkit-box",
                                WebkitLineClamp: isExpanded ? "unset" : 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {note.notes || t('components.notesTimeline.noComment')}
                            </Typography>
                          </Box>
                          <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={0.5} sx={{ minWidth: 140 }}>
                            <Typography variant="caption" color={'text.secondary'}>
                              {timestampLabel}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.primary[600] }}>
                              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              <Typography variant="caption" fontWeight={700}>
                                {isExpanded ? t('components.notesTimeline.collapse') : t('components.notesTimeline.expand')}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </ButtonBase>

                      <Collapse id={`scouting-note-${noteKey}`} in={isExpanded} timeout={0} unmountOnExit>
                        <Divider />
                        <Box sx={{ p: 2, pt: 1.5 }}>
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {note.titular && <Chip label={note.titular} size="small" />}
                              {note.posicion && <Chip label={note.posicion} size="small" variant="outlined" />}
                            </Stack>
                            <Button
                              variant="text"
                              startIcon={<OpenInFull />}
                              onClick={() => onOpenNote?.(note)}
                              sx={{ px: 0 }}
                            >
                              {t('components.notesTimeline.openNote')}
                            </Button>
                          </Stack>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NotesTimeline;
